import React, { useState, useEffect } from "react";
import {
    Button, Card, Form, Input, Alert, Checkbox, Row, Col,
    Typography, Flex, Steps, Divider,
} from "antd";
import {
    UserOutlined, FolderOutlined, FileTextOutlined, CheckCircleOutlined,
    MailOutlined, PhoneOutlined, IdcardOutlined, ShopOutlined,
    ArrowLeftOutlined, ArrowRightOutlined, ClockCircleOutlined,
    EnvironmentOutlined, AimOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import TopNavigation, { HEADER_HEIGHT } from "./snipets/TopNavigation";
import Footer               from "./snipets/Footer";
import { User }             from "../models/User";
import { Restaurant }       from "../models/Restaurant";
import { MyLocation }       from "../models/Location";
import { AuthenticationService } from "../services/AuthenticationService";
import { Bank, BankAccount }     from "../models/Bank";

import standard_bank from "../assets/standard_bank-logo_brandlogos.net_aqqyw.png";
import capitec       from "../assets/960px-Capitec_Bank_logo.svg.png";
import fnb           from "../assets/First_National_Bank_Logo.png";
import absa          from "../assets/ABSA_Logo.svg";
import africanBank   from "../assets/African_Bank_logo.svg";
import nedbank       from "../assets/Nedbank_logo.svg";
import tyme          from "../assets/TymeBank_Logo.jpg";
import ImageCropUpload from "../Utils/ImageCropUpload";

const { Title, Text, Link } = Typography;

const CAPITEC_BLUE = "#2F70EF";

// NOTE: mobile's BANKS list carries an `abbreviation` field the driver page's
// list didn't have (used on `bank.abbreviation` at submit time). Included
// here so the payload matches — but this is now duplicated in a third place
// (driver page, mobile, here). Worth pulling into one shared constants file.
const BANKS = [
    { key: "capitec", dbId: 1, abbreviation: "CAP",  name: "Capitec",       value: "capitec", recommended: true,  logo: capitec       },
    { key: "fnb",     dbId: 2, abbreviation: "FNB",  name: "FNB",           value: "fnb",     recommended: false, logo: fnb           },
    { key: "absa",    dbId: 3, abbreviation: "ABSA", name: "ABSA",          value: "absa",    recommended: false, logo: absa          },
    { key: "std",     dbId: 4, abbreviation: "STD",  name: "Standard Bank", value: "std",     recommended: false, logo: standard_bank },
    { key: "ned",     dbId: 5, abbreviation: "NED",  name: "Nedbank",       value: "ned",     recommended: false, logo: nedbank       },
    { key: "tyme",    dbId: 6, abbreviation: "TYME", name: "TymeBank",      value: "tyme",    recommended: false, logo: tyme          },
    { key: "african", dbId: 7, abbreviation: "AFR",  name: "African Bank",  value: "african", recommended: false, logo: africanBank   },
];

interface PersonalInfo {
    firstName: string; lastName: string; email: string; phone: string; idNumber: string;
}
interface BusinessInfo {
    businessName: string; businessAddress: string;
}
interface Documents { idDoc: File | null; bankProofDoc: File | null; }
interface Banking   { bankName: string; }

// ── Upload Box (plain, for documents — not images that need cropping) ──────

const UploadBox: React.FC<{ label: string; file: File | null; onSelect: (f: File) => void }> = ({ label, file, onSelect }) => {
    const ref = React.useRef<HTMLInputElement>(null);
    return (
        <div
            onClick={() => ref.current?.click()}
            style={{
                border: `2px dashed ${file ? "#00AF66" : "#D0D0D0"}`,
                borderRadius: 14, padding: "32px 20px", textAlign: "center",
                cursor: "pointer", background: file ? "#E6F7EF" : "#FAFAFA",
            }}
        >
            <input ref={ref} type="file" accept="image/*,.pdf" style={{ display: "none" }}
                   onChange={(e) => e.target.files?.[0] && onSelect(e.target.files[0])} />
            <CheckCircleOutlined style={{ fontSize: 32, color: file ? "#00AF66" : "#BDBDBD", display: "block", marginBottom: 12 }} />
            <Text strong style={{ color: file ? "#00AF66" : "#1A1A2E", display: "block", marginBottom: 4 }}>{label}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{file ? file.name : "Click to upload — JPG, PNG or PDF"}</Text>
        </div>
    );
};

// ── Bank Card ─────────────────────────────────────────────────────────────────

const BankCard: React.FC<{ bank: typeof BANKS[0]; selected: boolean; onSelect: () => void }> = ({ bank, selected, onSelect }) => (
    <div
        onClick={onSelect}
        style={{
            border: `2px solid ${selected ? "#00AF66" : bank.recommended ? CAPITEC_BLUE : "#E0E0E0"}`,
            borderRadius: 12, padding: "12px 8px", textAlign: "center",
            cursor: "pointer", background: selected ? "#E6F7EF" : "white",
            position: "relative",
        }}
    >
        {bank.recommended && (
            <div style={{
                position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                background: CAPITEC_BLUE, color: "white", fontSize: 9, fontWeight: 900,
                padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
            }}>
                ⚡ FASTEST PAYOUT
            </div>
        )}
        <div style={{ height: 36, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8 }}>
            <img src={bank.logo} alt={bank.name}
                 style={{ maxHeight: 32, maxWidth: "90%", objectFit: "contain" }}
                 onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
        </div>
        <Text style={{ fontSize: 11, fontWeight: selected ? 800 : 600, color: selected ? "#00AF66" : "#555" }}>
            {bank.name}
        </Text>
    </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const CONTENT_TOP_GAP = 32;

const SellerRegisterPage: React.FC = () => {
    const navigate = useNavigate();

    const [step,          setStep]          = useState(0);
    const [error,         setError]         = useState("");
    const [loading,       setLoading]       = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const [personal, setPersonal] = useState<PersonalInfo>({
        firstName: "", lastName: "", email: "", phone: "", idNumber: "",
    });
    const [business, setBusiness] = useState<BusinessInfo>({ businessName: "", businessAddress: "" });
    const [docs,     setDocs]     = useState<Documents>({ idDoc: null, bankProofDoc: null });
    const [banking,  setBanking]  = useState<Banking>({ bankName: "" });

    const [logoUrl,   setLogoUrl]   = useState("");
    const [bannerUrl, setBannerUrl] = useState("");
    const [coords,    setCoords]    = useState<{ lat: number; lng: number } | null>(null);
    const [locating,  setLocating]  = useState(false);

    useEffect(() => {
        AuthenticationService.getUserProfile().then((user: User) => {
            setPersonal((prev) => ({
                ...prev,
                firstName: user.name,
                lastName:  user.surname,
                email:     user.email,
                phone:     user.phone,
            }));
        }).catch(() => {});
    }, []);

    const setP = (field: keyof PersonalInfo) =>
        (e: any) => setPersonal((prev) => ({ ...prev, [field]: e.target?.value ?? e }));
    const setB = (field: keyof BusinessInfo) =>
        (e: any) => setBusiness((prev) => ({ ...prev, [field]: e.target?.value ?? e }));

    // ── Location ──────────────────────────────────────────────────────────────
    // NOTE: mobile uses expo-location's reverseGeocodeAsync to turn coords into
    // a readable address on-device. The browser Geolocation API only gives
    // coordinates — there's no built-in reverse geocoding. This currently just
    // captures coords and lets the person type the address themselves; if you
    // have a LocationService method for reverse geocoding (the LandingPage's
    // AddressSearch suggests one might exist for forward search), tell me and
    // I'll wire it in to auto-fill the address field the way mobile does.
    const handleUseCurrentLocation = () => {
        setLocating(true);
        setError("");
        if (!("geolocation" in navigator)) {
            setError("Your browser doesn't support location services. Please enter your address manually.");
            setLocating(false);
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                setLocating(false);
            },
            () => {
                setError("Could not get your location. Please enter your address manually.");
                setLocating(false);
            }
        );
    };

    // ── Validation ────────────────────────────────────────────────────────────
    const validateStep0 = () => {
        const { firstName, lastName, email, phone, idNumber } = personal;
        const { businessName, businessAddress } = business;
        if (!firstName || !lastName || !email || !phone || !idNumber || !businessName || !businessAddress) {
            setError("Please fill in all required fields."); return false;
        }
        if (!/\S+@\S+\.\S+/.test(email)) { setError("Please enter a valid email address."); return false; }
        if (!logoUrl)   { setError("Please upload a restaurant logo.");         return false; }
        if (!bannerUrl) { setError("Please upload a restaurant banner image."); return false; }
        if (!coords)    { setError('Please use "Use current location" or capture GPS coordinates for your address.'); return false; }
        setError(""); return true;
    };

    const validateStep1 = () => {
        if (!docs.idDoc)        { setError("Please upload your ID or passport.");        return false; }
        if (!banking.bankName)  { setError("Please select your bank.");                   return false; }
        if (!docs.bankProofDoc) { setError("Please upload your proof of bank account.");  return false; }
        setError(""); return true;
    };

    const validateStep2 = () => {
        if (!acceptedTerms) { setError("Please accept the terms and conditions to continue."); return false; }
        setError(""); return true;
    };

    const handleNext = () => {
        if (step === 0 && validateStep0()) setStep(1);
        if (step === 1 && validateStep1()) setStep(2);
    };

    const handleSubmit = async () => {
        if (!validateStep2()) return;
        setLoading(true);
        setError("");
        try {
            const idUpload        = await AuthenticationService.uploadFile(docs.idDoc!);
            const bankProofUpload = await AuthenticationService.uploadFile(docs.bankProofDoc!);

            const selectedBankData = BANKS.find(b => b.value === banking.bankName)!;
            const bank             = new Bank();
            bank.id                = selectedBankData.dbId;
            bank.abbreviation      = selectedBankData.abbreviation;
            const account          = new BankAccount();
            account.bank           = bank;

            const user           = new User();
            user.name            = personal.firstName;
            user.surname         = personal.lastName;
            user.email           = personal.email;
            user.phone           = personal.phone;
            user.id_number       = personal.idNumber;
            user.id_document     = idUpload.url;
            user.bank_statement  = bankProofUpload.url;
            //user.bank_account    = account;

            const location     = new MyLocation();
            location.latitude  = String(coords?.lat ?? 0);
            location.longitude = String(coords?.lng ?? 0);

            const restaurant        = new Restaurant();
            restaurant.name         = business.businessName;
            restaurant.email        = personal.email;
            restaurant.phone_number = personal.phone;
            restaurant.location     = location;
            restaurant.owner        = user;
            restaurant.logo_url     = logoUrl;
            restaurant.banner_url   = bannerUrl;

            await AuthenticationService.registerSeller({ user, restaurant });
            setStep(3);
        } catch (err: any) {
            const s = err?.response?.status || err?.status;
            if (s === 401)      setError("Not authorized. Please log in again.");
            else if (s === 400) setError("Invalid data. Please check your inputs.");
            else if (s === 404) setError("No account found with this email. Please create a regular account first, then apply to sell.");
            else if (s === 409) setError("An account with this email already exists.");
            else if (s >= 500)  setError("Server error. Please try again later.");
            else                setError("Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <TopNavigation />

            <Flex
                align="flex-start"
                justify="center"
                style={{
                    minHeight: "100dvh",
                    background: "#f5f5f0",
                    padding: `${HEADER_HEIGHT + CONTENT_TOP_GAP}px clamp(16px, 4vw, 24px) 40px`,
                }}
            >
                <div style={{ width: "100%", maxWidth: 680 }}>

                    {step < 3 && (
                        <>
                            <Title level={3} style={{ margin: "0 0 16px" }}>Seller Registration</Title>
                            <Steps
                                current={step}
                                responsive={false}
                                size="small"
                                style={{ marginBottom: 24 }}
                                items={[
                                    { title: "Business", icon: <ShopOutlined />     },
                                    { title: "Documents", icon: <FolderOutlined />  },
                                    { title: "Terms",     icon: <FileTextOutlined />},
                                ]}
                            />
                        </>
                    )}

                    <Card>
                        {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20 }} />}

                        {/* ── Step 0: Personal + Business ── */}
                        {step === 0 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 16 }}>
                                    <UserOutlined /> Personal Information
                                </Title>
                                <Form layout="vertical">
                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="First Name *">
                                                <Input size="large" placeholder="First name" value={personal.firstName} onChange={setP("firstName")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Last Name *">
                                                <Input size="large" placeholder="Last name" value={personal.lastName} onChange={setP("lastName")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Email Address *">
                                                <Input size="large" prefix={<MailOutlined />} placeholder="email@example.com" type="email" value={personal.email} onChange={setP("email")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Phone Number *">
                                                <Input size="large" prefix={<PhoneOutlined />} placeholder="0XX XXX XXXX" type="tel" value={personal.phone} onChange={setP("phone")} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24}>
                                            <Form.Item label="ID Number *">
                                                <Input size="large" prefix={<IdcardOutlined />} placeholder="ID number" value={personal.idNumber}
                                                       onChange={(e) => setPersonal(p => ({ ...p, idNumber: e.target.value.replace(/[^0-9]/g, "") }))} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </Form>

                                <Divider style={{ margin: "8px 0 20px" }} />

                                <Title level={5} type="success" style={{ marginBottom: 16 }}>
                                    <ShopOutlined /> Business Information
                                </Title>

                                <Form layout="vertical">
                                    <Form.Item label="Business Name *" style={{ marginBottom: 20 }}>
                                        <Input size="large" placeholder="e.g. Mama's Kitchen" value={business.businessName} onChange={setB("businessName")} />
                                    </Form.Item>
                                </Form>

                                <Row gutter={16} style={{ marginBottom: 20 }}>
                                    <Col xs={24} sm={12}>
                                        <ImageCropUpload
                                            label="Restaurant Logo (square) *"
                                            aspect={1}
                                            boxWidth={140} boxHeight={140}
                                            filePrefix="logo"
                                            value={logoUrl}
                                            onChange={setLogoUrl}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <ImageCropUpload
                                            label="Restaurant Banner (4:3) *"
                                            aspect={4 / 3}
                                            boxWidth="100%" boxHeight={140}
                                            filePrefix="banner"
                                            value={bannerUrl}
                                            onChange={setBannerUrl}
                                        />
                                    </Col>
                                </Row>

                                <Text strong style={{ display: "block", marginBottom: 10 }}>Business Location *</Text>

                                <Button
                                    block size="large"
                                    icon={<AimOutlined />}
                                    loading={locating}
                                    onClick={handleUseCurrentLocation}
                                    style={{ marginBottom: 12, borderColor: "#00AF66", color: "#00AF66", background: "#E6F7EF" }}
                                >
                                    {locating ? "Getting location…" : "Use current location"}
                                </Button>

                                <Input
                                    size="large"
                                    prefix={<EnvironmentOutlined />}
                                    placeholder="Business street address"
                                    value={business.businessAddress}
                                    onChange={setB("businessAddress")}
                                    style={{ marginBottom: coords ? 12 : 24 }}
                                />

                                {coords && (
                                    <Flex align="center" gap={6} style={{ background: "#E6F7EF", borderRadius: 8, padding: "8px 12px", marginBottom: 24 }}>
                                        <CheckCircleOutlined style={{ color: "#00AF66", fontSize: 13 }} />
                                        <Text style={{ color: "#00AF66", fontSize: 12 }}>
                                            GPS captured: {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                                        </Text>
                                    </Flex>
                                )}

                                <Button type="primary" size="large" block onClick={handleNext} icon={<ArrowRightOutlined />} iconPosition="end">
                                    Next — Documents &amp; Banking
                                </Button>
                            </>
                        )}

                        {/* ── Step 1: Documents & Banking ── */}
                        {step === 1 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 20 }}>
                                    <FolderOutlined /> Document Upload
                                </Title>

                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 20 }}
                                    message="Please upload clear copies of your ID and proof of bank account. Accepted formats: JPG, PNG, PDF."
                                />

                                <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
                                    <Col xs={24} sm={12}>
                                        <UploadBox label="Owner ID / Passport" file={docs.idDoc}
                                                   onSelect={(f) => setDocs((d) => ({ ...d, idDoc: f }))} />
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <UploadBox label="Proof of Bank Account" file={docs.bankProofDoc}
                                                   onSelect={(f) => setDocs((d) => ({ ...d, bankProofDoc: f }))} />
                                    </Col>
                                </Row>

                                <Title level={5} type="success" style={{ marginBottom: 12 }}>Banking Details</Title>

                                <Alert
                                    type="info"
                                    style={{ marginBottom: 20, borderLeft: `4px solid ${CAPITEC_BLUE}`, background: "#EEF4FE" }}
                                    message={<Text strong style={{ color: CAPITEC_BLUE }}>Faster Payouts with Capitec</Text>}
                                    description="We highly recommend using a Capitec Bank account — payouts are processed significantly faster compared to other banks."
                                    icon={<img src={capitec} alt="Capitec" style={{ width: 32, objectFit: "contain" }} />}
                                    showIcon
                                />

                                <Text strong style={{ display: "block", marginBottom: 12 }}>Select Your Bank *</Text>
                                <Row gutter={[12, 12]} style={{ marginBottom: 32 }}>
                                    {BANKS.map((bank) => (
                                        <Col key={bank.key} xs={12} sm={6}>
                                            <BankCard
                                                bank={bank}
                                                selected={banking.bankName === bank.value}
                                                onSelect={() => setBanking({ bankName: bank.value })}
                                            />
                                        </Col>
                                    ))}
                                </Row>

                                <Row gutter={12}>
                                    <Col xs={24} sm={8}>
                                        <Button size="large" block icon={<ArrowLeftOutlined />} onClick={() => { setError(""); setStep(0); }}>
                                            Back
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Button type="primary" size="large" block onClick={handleNext} icon={<ArrowRightOutlined />} iconPosition="end">
                                            Next — Terms &amp; Conditions
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* ── Step 2: Terms ── */}
                        {step === 2 && (
                            <>
                                <Title level={5} type="success" style={{ marginBottom: 20 }}>
                                    <FileTextOutlined /> Terms &amp; Conditions
                                </Title>

                                <div style={{
                                    background: "#f5f5f0", borderRadius: 10, border: "1px solid #E0E0E0",
                                    padding: "16px 20px", marginBottom: 24,
                                    maxHeight: 360, overflowY: "auto",
                                    fontSize: 13, color: "#555", lineHeight: 1.8,
                                }}>
                                    <p><strong>1. Seller Agreement</strong><br />By registering as a seller, you agree to list only legitimate products or services. You are solely responsible for the accuracy of your listings.</p>
                                    <p><strong>2. Commission &amp; Payouts</strong><br />A commission fee is deducted from each completed order. Payouts are processed weekly to your registered bank account.</p>
                                    <p><strong>3. Product Standards</strong><br />All products and services must comply with South African consumer protection laws. We reserve the right to remove listings that violate our policies.</p>
                                    <p><strong>4. Business Verification</strong><br />Your submitted documents will be reviewed within 5 working days. Your store will only go live after successful verification.</p>
                                    <p><strong>5. Code of Conduct</strong><br />You agree to fulfil orders promptly and maintain a professional standard of service. Poor performance may result in suspension.</p>
                                    <p><strong>6. Data &amp; Privacy</strong><br />Your business and personal information is processed in accordance with our Privacy Policy and POPIA.</p>
                                    <p><strong>7. Termination</strong><br />Either party may terminate this agreement. We reserve the right to deactivate your account for policy violations without prior notice.</p>
                                </div>

                                <Checkbox checked={acceptedTerms} onChange={() => setAcceptedTerms(!acceptedTerms)} style={{ marginBottom: 24 }}>
                                    <Text strong>I have read and agree to the Seller Terms &amp; Conditions</Text>
                                </Checkbox>

                                <Row gutter={12}>
                                    <Col xs={24} sm={8}>
                                        <Button size="large" block icon={<ArrowLeftOutlined />} onClick={() => { setError(""); setStep(1); }}>
                                            Back
                                        </Button>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                        <Button type="primary" size="large" block loading={loading} disabled={loading || !acceptedTerms} onClick={handleSubmit}>
                                            <CheckCircleOutlined /> Submit Application
                                        </Button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* ── Step 3: Success ── */}
                        {step === 3 && (
                            <Flex vertical align="center" gap="large" style={{ padding: "32px 0", textAlign: "center" }}>
                                <div style={{
                                    width: 96, height: 96, borderRadius: "50%",
                                    background: "#E6F7EF", border: "3px solid #00AF66",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                    <CheckCircleOutlined style={{ fontSize: 48, color: "#00AF66" }} />
                                </div>

                                <div>
                                    <Title level={3} style={{ margin: "0 0 4px" }}>Application Submitted!</Title>
                                    <Title level={5} type="success" style={{ margin: 0 }}>Welcome aboard 🎉</Title>
                                </div>

                                <Card style={{ textAlign: "left", width: "100%" }}>
                                    {[
                                        { icon: <ClockCircleOutlined />, title: "Review in progress", desc: <>Our team will verify your documents within <Text strong>5 working days</Text>.</> },
                                        { icon: <MailOutlined />,        title: "Check your email",   desc: "We'll notify you once your seller account is approved and live." },
                                        { icon: <PhoneOutlined />,       title: "Need help?",         desc: "Contact our seller support team with any questions about your application." },
                                    ].map((item, i) => (
                                        <Flex key={i} gap={12} align="flex-start" style={{ marginBottom: i < 2 ? 16 : 0 }}>
                                            <span style={{ color: "#00AF66", fontSize: 20, marginTop: 2 }}>{item.icon}</span>
                                            <div>
                                                <Text strong style={{ display: "block" }}>{item.title}</Text>
                                                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.7 }}>{item.desc}</Text>
                                            </div>
                                        </Flex>
                                    ))}
                                </Card>

                                <Button type="primary" size="large" block onClick={() => navigate("/")}>
                                    Back to Home
                                </Button>
                            </Flex>
                        )}
                    </Card>

                    {step < 3 && (
                        <Flex gap={4} style={{ marginTop: 16 }}>
                            <Text type="secondary">Already have an account?</Text>
                            <Link onClick={() => navigate("/login")}>Sign In</Link>
                        </Flex>
                    )}
                </div>
            </Flex>

            <Footer />
        </>
    );
};

export default SellerRegisterPage;